// components/MasteryRing.tsx
//
// W5 Session 1 — shared mastery-ring component used on BOTH the instructor
// surface (`StudentDetailScreen` Tab 1 Progresso) AND the student surface
// (`HomeScreen` `<IndicePreparazioneCard />` companions + the
// `MioProgressoScreen` 25-ring grid).
//
// Visual contract (P6 STEP 3d — 5-band model, founder D2):
//   - Inline conic-gradient ring; percentage label inside.
//   - Color + tier come from the single source of truth `masteryBand()` in
//     tokens: Forte (sage ≥0.85) / Buono (violet 0.70–0.84) / Da rinforzare
//     (amber 0.40–0.69) / Debole (rose <0.40) / Da iniziare (grey/empty —
//     no principles or none observed yet; ring stroke falls back to textMuted).
//   - aria-label per WCAG 1.4.1 — color-independent description of the
//     ring's state for screen readers; data-ring-tone carries the band id.
//   - data-testid="ring-${argomentoId}" + data-testid="ring-tier-${argomentoId}"
//     so existing instructor-side tests + student-side tests can pin rings.
//
// Behaviour is purely presentational — props drive every ring; no fetch,
// no state.

import type { JSX } from 'react';
import { useTokens } from '../state/theme';
import { masteryBand, masteryBandColors, MASTERY_BANDS } from '../tokens';

/**
 * 5-band mastery label (founder D2 "5-tier everywhere" + the 2026-06-06 cutoffs):
 *   Forte ≥0.85 · Buono 0.70–0.84 · Da rinforzare 0.40–0.69 · Debole <0.40 ·
 *   Da iniziare = not started (no principles, or none observed yet).
 * Replaces the W6 v1.1 3-tier Forte/Medio/Debole mapping (the "strong" bar
 * moved 0.70→0.85 — a deliberate, founder-approved threshold change per spec
 * §3.5). Single source of truth = `masteryBand()` in tokens.
 *
 * Exported separately so callers needing the label-only path (e.g. a
 * non-ring summary line) reuse the same source-of-truth instead of forking it.
 */
export function masteryTierLabel(arg: { average_mastery: number; principles_total: number; principles_observed?: number }): string {
  return masteryBand({
    averageMastery: arg.average_mastery,
    principlesTotal: arg.principles_total,
    principlesObserved: arg.principles_observed,
  }).label;
}

export interface MasteryRingProps {
  argomentoId:        string;
  nameIt:             string;
  /** 0..1; rendered as a percentage. For argomenti, this is coverage-aware readiness. */
  averageMastery:     number;
  principlesTotal:    number;
  principlesObserved?: number;
  principlesMastered: number;
  readinessLabel?:    string;
  nextReason?:         string;
  onAskProf?: () => void;
}

export function MasteryRing({
  argomentoId,
  nameIt,
  averageMastery,
  principlesTotal,
  principlesObserved,
  principlesMastered,
  readinessLabel,
  nextReason,
  onAskProf,
}: MasteryRingProps): JSX.Element {
  const tokens = useTokens();
  const pct = Math.round(averageMastery * 100);
  const observedCount = principlesObserved ?? principlesTotal;
  const unobserved = principlesTotal > 0 && observedCount === 0;
  // 5-band model (founder D2) — single source of truth in tokens.
  const band = masteryBand({ averageMastery, principlesTotal, principlesObserved });
  const isEmptyBand = band.id === MASTERY_BANDS.daIniziare.id;
  const bandColors = masteryBandColors(band, tokens.mode);
  const ringTone = band.id; // data-ring-tone now carries the band id
  // Ring stroke = vivid band color (textMuted for the empty band so the white
  // 'da iniziare' fill stays visible on the card). Tier text = contrast-safe
  // band text color (WCAG AA) for small labels.
  const ringColor = isEmptyBand ? tokens.textMuted : bandColors.color;
  const tierColor = isEmptyBand ? tokens.textMuted : bandColors.textColor;
  const tier = band.label;
  const observedText = `${observedCount}/${principlesTotal} osservati`;
  const evidenceText = (principlesTotal === 0 || unobserved)
    ? observedText
    : `${observedText} - ${principlesMastered}/${principlesTotal} padroneggiati`;
  // Aria-label format: announces argomento name + percentage + tier + n/N.
  // Color-independent per WCAG 1.4.1.
  let ariaLabel = unobserved
    ? `${nameIt}: non ancora osservato. ${observedCount} principi osservati su ${principlesTotal}.`
    : `${nameIt}: ${pct}% — livello ${tier}. ${principlesMastered} principi padroneggiati su ${principlesTotal}.`;
  if (principlesTotal === 0) {
    ariaLabel = `${nameIt}: nessun dato. ${principlesMastered} principi padroneggiati su ${principlesTotal}.`;
  }
  const reasonLine = nextReason ? (
    <div data-testid={`ring-reason-${argomentoId}`} style={{ marginTop: 4, fontSize: 11, color: tokens.textMuted, lineHeight: 1.35 }}>
      {nextReason}
    </div>
  ) : null;
  if (onAskProf) {
    return (
      <button
        type="button"
        data-testid={`ring-${argomentoId}`}
        data-ring-tone={ringTone}
        aria-label={`Ripassa con Il Prof: ${nameIt}. ${evidenceText}. ${readinessLabel ?? tier}.${nextReason ? ` ${nextReason}` : ''}`}
        title={ariaLabel}
        onClick={onAskProf}
        style={{
          padding:      '12px',
          background:   tokens.surface,
          border:       `1px solid ${tokens.line}`,
          borderRadius: tokens.radius.md,
          color:        tokens.text,
          cursor:       'pointer',
          fontFamily:   tokens.fonts.body,
          textAlign:    'left',
          width:        '100%',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            aria-label={ariaLabel}
            role="img"
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: `conic-gradient(${ringColor} ${pct}%, ${tokens.line} ${pct}%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 700,
              color: tokens.text,
              position: 'relative',
            }}
          >
            <span style={{
              position: 'absolute',
              background: tokens.surface,
              width: 26,
              height: 26,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
            }}>
              {pct}%
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {nameIt}
            </div>
            <div style={{ fontSize: 11, color: tokens.textMuted, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span data-testid={`ring-tier-${argomentoId}`} style={{ color: tierColor, fontWeight: 600 }}>
                {tier}
              </span>
              <span aria-hidden="true">/</span>
              <span>{principlesMastered}/{principlesTotal}</span>
              {readinessLabel && (
                <>
                  <span aria-hidden="true">/</span>
                  <span data-testid={`ring-state-${argomentoId}`} style={{ color: tokens.text, fontWeight: 600 }}>
                    {readinessLabel}
                  </span>
                </>
              )}
            </div>
            <div data-testid={`ring-evidence-${argomentoId}`} style={{ marginTop: 3, fontSize: 11, color: tokens.textMuted, lineHeight: 1.35 }}>
              {evidenceText}
            </div>
            {reasonLine}
            <div style={{ marginTop: 5, fontSize: 11, color: tokens.accent, fontWeight: 700 }}>
              Parlane col Prof
            </div>
          </div>
        </div>
      </button>
    );
  }
  return (
    <div
      data-testid={`ring-${argomentoId}`}
      data-ring-tone={ringTone}
      title={ariaLabel}
      style={{
        padding:      '12px',
        background:   tokens.surface,
        border:       `1px solid ${tokens.line}`,
        borderRadius: tokens.radius.md,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          aria-label={ariaLabel}
          role="img"
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: `conic-gradient(${ringColor} ${pct}%, ${tokens.line} ${pct}%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 700,
            color: tokens.text,
            position: 'relative',
          }}
        >
          <span style={{
            position: 'absolute',
            background: tokens.surface,
            width: 26,
            height: 26,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
          }}>
            {pct}%
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {nameIt}
          </div>
          <div style={{ fontSize: 11, color: tokens.textMuted, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span data-testid={`ring-tier-${argomentoId}`} style={{ color: tierColor, fontWeight: 600 }}>
              {tier}
            </span>
            <span aria-hidden="true">/</span>
            <span>{principlesMastered}/{principlesTotal}</span>
            {readinessLabel && (
              <>
                <span aria-hidden="true">/</span>
                <span data-testid={`ring-state-${argomentoId}`} style={{ color: tokens.text, fontWeight: 600 }}>
                  {readinessLabel}
                </span>
              </>
            )}
          </div>
          <div data-testid={`ring-evidence-${argomentoId}`} style={{ marginTop: 3, fontSize: 11, color: tokens.textMuted, lineHeight: 1.35 }}>
            {evidenceText}
          </div>
          {reasonLine}
        </div>
      </div>
    </div>
  );
}
